import type { BanDich } from '../../types';
import type { viNktvChung } from './chung.vi';

/**
 * Bản tiếng Anh của `chung.vi.ts`. Câu của `nktvDanhMuc` giữ NGUYÊN placeholder một cặp ngoặc của danh mục
 * (`{doi_tuong}`, `{du_lieu_them.x}`); `danh-muc-khop.test.ts` đòi hai bên cùng bộ placeholder. Câu nhật ký viết ở thì
 * quá khứ ("Signed document …") — đó là việc đã xảy ra.
 */
export const enNktvChung: Pick<BanDich, keyof typeof viNktvChung> = {
  nktv: {
    trangThai: {
      thuLai: 'Try again',
      taiThem: 'Load more',
    },
    loi: {
      xemCheoTieuDe: "You're viewing the tenant interface",
      xemCheoMo:
        "The AI usage log reads one organization's data. Platform staff accounts don't belong to any tenant, so this screen has no data — sign in with an organization account to see it.",
      chuaSanSangTieuDe: 'The AI usage log is not ready',
      chuaSanSangMo: "audit-service isn't serving this module on this server yet. Try again later or contact the operations team.",
    },
    cau: {
      trong: '(empty)',
      co: 'yes',
      khong: 'no',
      khongRo: "This action isn't in this version's catalog yet ({{ma}})",
      llmChuaDoToken: 'Called AI assistant ({{tinhNang}}) · tokens not measured',
      llmKhongRoTinhNang: 'unknown feature',
    },
    giaTri: {
      co: 'Yes',
      khong: 'No',
    },
    loc: {
      moc: 'Time range',
      lamMoi: 'Refresh',
      xoaLoc: 'Clear filters',
      tuGio: 'From date and time',
      denGio: 'To date and time',
      biCat:
        'Suggestions show at most 500 values per group — narrow the time range to see them all. Filters still apply correctly to the values you pick.',
    },
    mocKhoang: {
      '24_gio': 'Last 24 hours',
      '7_ngay': 'Last 7 days',
      '30_ngay': 'Last 30 days',
      thang_nay: 'This month',
      tuy_chon: 'Custom',
    },
    khoang: {
      biKep: 'The time range was trimmed to the last 90 days — the longest range a summary can cover.',
    },
    ketQua: {
      that_bai: 'Failed',
    },
    nguoi: {
      khongXacDinh: '(unidentified)',
    },
    tang: {
      gateway: 'Gateway',
      nghiep_vu: 'Business',
    },
    nganKeo: {
      moLangfuse: 'Open trace in Langfuse',
    },
    chatLuong: {
      tieuDe: 'Data quality',
      moBangGia: 'Open model pricing',
      chuaCoGia: '{{so}} calls have no price, so every amount below is missing that part.',
      thieuTyGia:
        '{{so}} calls have a price but no exchange rate into the reporting currency, so every amount below is missing that part. Exchange rates are entered by platform admins.',
      coCanhBao:
        "{{so}} calls raised warnings during token conversion — their token counts aren't reliable yet. See each call in the Model calls tab.",
      uocTinh: '{{so}} calls use estimated tokens, not the counts the provider returned.',
      daDu: 'Every call in this period has a price, and none raised a token conversion warning.',
    },
    loaiToken: {
      dau_vao: 'Input',
      dau_vao_anh: 'Image input',
      dau_vao_tool: 'Tool input',
      dau_vao_dem: 'Cache read',
      ghi_dem: 'Cache write',
      dau_ra: 'Output',
      suy_luan: 'Reasoning',
    },
    loaiTokenMoTa: {
      dau_vao: 'Input tokens excluding images, tools and cache reads — those three are counted separately.',
      dau_vao_anh:
        'Tokens for images in the prompt. Images read from cache count as “Cache read”, because the cache takes precedence over content type.',
      dau_vao_tool:
        'Tokens for tool declarations in the prompt. Empty in most deployments: only Gemini reports this number, and only on flows that call tools.',
      dau_vao_dem: 'Input tokens read from cache — cheaper than regular input tokens.',
      ghi_dem: 'Tokens written to cache — pricier than regular input tokens, and the part subtracted in the cache savings formula.',
      dau_ra: 'Tokens the model generated.',
      suy_luan: 'Reasoning tokens the provider reports separately. Empty for Anthropic: reasoning is counted within output tokens.',
    },
  },
  nktvDanhMuc: {
    xac_thuc: {
      dang_nhap: 'Signed in',
      dang_nhap_that_bai: 'Failed to sign in',
      dang_xuat: 'Signed out',
    },
    nguoi_dung: {
      tao: 'Created account {doi_tuong}',
      khoa: 'Locked account {doi_tuong}',
      doi_quyen: 'Changed permissions of {doi_tuong}: {thay_doi.vai_tro.truoc} → {thay_doi.vai_tro.sau}',
    },
    van_ban: {
      tao: 'Created official document {doi_tuong}',
      sua: 'Edited official document {doi_tuong}',
      trinh: 'Submitted official document {doi_tuong}',
      ky: 'Signed official document {doi_tuong}',
      ban_hanh: 'Issued official document {doi_tuong}',
      thu_hoi: 'Withdrew official document {doi_tuong}',
      xoa: 'Deleted official document {doi_tuong}',
    },
    ho_so: {
      xem: 'Viewed record {doi_tuong}',
    },
    du_lieu: {
      xuat: 'Exported {du_lieu_them.so_ban_ghi} records of {doi_tuong}',
    },
    chung_tu: {
      ocr: 'Extracted data from document {doi_tuong}',
      doi_chieu: 'Reconciled document {doi_tuong}',
      tao: 'Uploaded document {doi_tuong}',
      sua: 'Edited {du_lieu_them.so_truong} fields of document {doi_tuong}',
      luu_nhap: 'Saved draft of document {doi_tuong}',
      ocr_lai: 'Rescanned document {doi_tuong}',
      ocr_treo: 'Document {doi_tuong} timed out while scanning and was moved to Error',
      canh_bao_kiem_tra: 'Document {doi_tuong} has {du_lieu_them.so_canh_bao} validation warnings',
      xac_nhan: 'Confirmed document {doi_tuong}',
      chuyen_kho: 'Moved document {doi_tuong} to the knowledge base',
      xoa: 'Deleted document {doi_tuong}',
    },
    llm: {
      goi: 'Called AI assistant ({du_lieu_them.tinh_nang}) · {du_lieu_them.tong_token} tokens',
    },
    cau_hinh: {
      sua: 'Changed settings {doi_tuong}',
    },
    bang_gia: {
      sua: 'Changed token price of model {doi_tuong}',
    },
    ty_gia: {
      sua: 'Updated exchange rate {doi_tuong}',
    },
    han_muc: {
      sua: 'Changed AI quota of {doi_tuong}',
      canh_bao: '{doi_tuong} has used {du_lieu_them.phan_tram}% of the AI quota',
      vuot: '{doi_tuong} exceeded the AI quota',
      chan: 'Blocked {du_lieu_them.so_luot_bi_chan} new AI calls because {doi_tuong} was used up for period {du_lieu_them.han_muc.ky}',
    },
    chi_phi: {
      dieu_chinh: 'Recalculated AI cost for period {doi_tuong}',
    },
    nhat_ky: {
      xem: 'Viewed logs',
      xuat: 'Exported logs',
      luu_tru: 'Archived logs for period {doi_tuong}',
      sai_lech_toan_ven: 'Detected a log integrity mismatch',
    },
    canh_bao: {
      phat: 'Raised alert: {doi_tuong}',
      xu_ly: 'Handled alert {doi_tuong}: {thay_doi.trang_thai.truoc} → {thay_doi.trang_thai.sau}',
      sua_quy_tac: 'Edited alert rule {doi_tuong}',
    },
    danh_muc: {
      tao: 'Created catalog item {doi_tuong}',
      sua: 'Edited catalog item {doi_tuong}',
      xoa: 'Deleted catalog item {doi_tuong}',
    },
    bo_tieu_chi: {
      tao: 'Created criteria set {doi_tuong}',
      sua: 'Edited criteria set {doi_tuong}',
      luu_nhap: 'Saved draft of criteria set {doi_tuong}',
      phat_hanh: 'Published criteria set {doi_tuong} version {du_lieu_them.so}',
      cap_duyet: 'Changed approval levels of criteria set {doi_tuong}: {du_lieu_them.so_cap} levels',
      chay_thu: 'Test-ran criteria set {doi_tuong}',
      ai_goi_y: 'Asked AI for suggestions on criteria set {doi_tuong}',
      ai_can_trong_so: 'Asked AI to balance the weights of criteria set {doi_tuong}',
      xoa: 'Deleted criteria set {doi_tuong}',
    },
    tham_dinh: {
      tao: 'Created appraisal case {doi_tuong} with {du_lieu_them.so_tep} files',
      nop_lai: 'Resubmitted appraisal case {doi_tuong}',
      sua_ket_qua: 'Edited the result of criterion {du_lieu_them.ma_tieu_chi} in appraisal case {doi_tuong}',
      cham_lai: 'Re-scored appraisal case {doi_tuong}',
      hoan_tat: 'Completed appraisal {doi_tuong}: {du_lieu_them.diem} points · {du_lieu_them.ket_luan}',
      phe_duyet: 'Approved appraisal case {doi_tuong} at level {du_lieu_them.cap}',
      tra_lai: 'Returned appraisal case {doi_tuong} at level {du_lieu_them.cap}',
      xuat_bien_ban: 'Exported appraisal minutes {doi_tuong}',
      ai_viet_lai: 'AI assistant rewrote the assessment of appraisal case {doi_tuong} (attempt {du_lieu_them.lan})',
      quyet_goi_y:
        'Decided on the suggested edit to criterion {du_lieu_them.ma_tieu_chi} of appraisal case {doi_tuong}: {du_lieu_them.quyet_dinh}',
      ap_dung_sua: 'Applied {du_lieu_them.so_goi_y} suggested edits to appraisal case {doi_tuong}',
      xoa: 'Deleted appraisal case {doi_tuong}',
    },
    khoa_api: {
      tao: 'Issued API key {doi_tuong}',
      sua_webhook: 'Changed the webhook of API key {doi_tuong}',
      thu_webhook: 'Tested the webhook of API key {doi_tuong}',
      thu_hoi: 'Revoked API key {doi_tuong}',
    },
    nhiem_vu: {
      tao: 'Created task {doi_tuong}',
      sua: 'Edited task {doi_tuong}: {du_lieu_them.truong_doi}',
      them_viec_con: 'Added a subtask to task {doi_tuong}: {du_lieu_them.viec_con}',
      doi_nguoi_tham_gia:
        'Changed participants of task {doi_tuong}: added {du_lieu_them.so_them}, removed {du_lieu_them.so_bo}',
      doi_trang_thai: 'Changed the status of task {doi_tuong}: {thay_doi.trang_thai.truoc} → {thay_doi.trang_thai.sau}',
      bao_tien_do: 'Reported progress on task {doi_tuong}: {du_lieu_them.tien_do}%',
      y_kien: 'Commented on task {doi_tuong}',
      xoa: 'Deleted task {doi_tuong}',
    },
  },
};
